import {PageTitle} from 'layout/master-layout'
import {
 useCallback,
 useEffect,
 useMemo,
 useRef,
 useState,
} from 'react'
import UploadThemeImage from '../common/UploadThemeImage'
import TableTemplate from './TableTemplate'
import TemplateDialog from './TemplateDialog'
import VariableNote from '../common/VariableNote'
import SkuSizeDropdown from './SkuSizeDropDown'
import {
 LanguageItem,
 Product,
 PromotionFormState,
 TemplateRow,
} from 'models/CreatePromotion'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {
 createPromotionApi,
 deleteCampaignVariantApi,
 fetchCampaignDetail,
 fetchLanguageList,
 updateCampaignApi,
} from 'utils/api/auth/productPromotionService'
import {fetchProductList} from 'utils/api/productManagementService'
import {uploadFile} from 'utils/api/auth/mediaService'
import {debounce} from 'lodash'
import {toast} from 'react-toastify'
import Preview from '../common/Preview'
import PromotionImageSection from './PromotionImageSection'

type IProps = {
 hidePartent: boolean
 setHideParent: (newState: boolean) => void
}

const CreatePromotion = (props: IProps) => {
 const {setHideParent} = props
 const navigate = useNavigate()
 const {t} = useTranslation()
 const {id: campaignId} = useParams()
 const isEditMode = !!campaignId
 const [formState, setFormState] =
  useState<PromotionFormState>({
   campaign_variants: [],
  })
 const templates = formState.campaign_variants ?? []
 const [showDialog, setShowDialog] = useState(false)
 const [editRow, setEditRow] = useState<TemplateRow | null>(
  null
 )
 const [localImagePreview, setLocalImagePreview] = useState<
  string | null
 >(null)
 const [showDropdown, setShowDropdown] = useState(false)
 const [imageLoader, setImageLoader] = useState(false)
 const [products, setProducts] = useState<Product[]>([])
 const [languageMap, setLanguageMap] = useState<
  Record<string, LanguageItem>
 >({})
 const [showPreview, setShowPreview] = useState(false)

 const wrapperRef = useRef<HTMLDivElement>(null)
 const originalImageRef = useRef<string | null>(null)
 const [selectedLang, setSelectedLang] = useState<
  string | null
 >(null)

 const canShowPreview = useMemo(
  () =>
   !!formState?.image &&
   !!formState?.campaign_variants &&
   formState.campaign_variants.length > 0,
  [formState.campaign_variants, formState.image]
 )

 const languageVariants = useMemo(
  () =>
   formState?.campaign_variants?.map((v) => ({
    language_code: v.lang?.language_code ?? '',
    language: v.lang?.language ?? '',
   })) ?? [],
  [formState.campaign_variants]
 )

 const selectedTemplate =
  formState?.campaign_variants?.find(
   (v) => v.lang?.language_code === selectedLang
  ) ?? null
 const VARIABLE_LIST = [
  {
   label: t('createPromotion.variables.variable1.label'),
   value: t('createPromotion.variables.variable1.value'),
  },
  {
   label: t('createPromotion.variables.variable2.label'),
   value: t('createPromotion.variables.variable2.value'),
  },
  {
   label: t('createPromotion.variables.variable3.label'),
   value: t('createPromotion.variables.variable3.value'),
  },
 ]
 const updateState = <K extends keyof PromotionFormState>(
  name: K,
  value: PromotionFormState[K]
 ) => {
  setFormState((prev) => ({
   ...prev,
   [name]: value,
  }))
 }

 useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
   if (
    wrapperRef.current &&
    !wrapperRef.current.contains(e.target as Node)
   ) {
    setShowDropdown(false)
   }
  }

  document.addEventListener('mousedown', handleClickOutside)
  return () =>
   document.removeEventListener(
    'mousedown',
    handleClickOutside
   )
 }, [])

 useEffect(() => {
  if (languageVariants.length === 0) {
   setSelectedLang(null)
   return
  }

  const exists = languageVariants.some(
   (l) => l.language_code === selectedLang
  )

  if (!exists) {
   setSelectedLang(languageVariants[0].language_code)
  }
 }, [languageVariants, selectedLang])

 const isFormValid = useMemo(() => {
  if (isEditMode) {
   return (
    (!!formState.campaign_variants &&
     formState.campaign_variants.length > 0) ||
    !!formState.image
   )
  }
  return (
   !!formState.productName &&
   !!formState.name &&
   !!formState.image &&
   !!formState.campaign_variants &&
   formState.campaign_variants.length > 0
  )
 }, [formState, isEditMode])

 const getProducts = async (search: string) => {
  const payload = {
   pageSize: 1000,
   dropdown: true,
   search,
  }

  const res = await fetchProductList(payload)
  if (res?.results) {
   setProducts(res.results)
  }
 }

 const loadCampaignDetail = useCallback(
  async (id: number) => {
   const res = await fetchCampaignDetail(id)
   if (!res.data) return

   const {
    image_url,
    campaign_variant,
    product_name,
    product_variant_size,
    name,
   } = res.data

   const mappedVariants: TemplateRow[] = (
    campaign_variant ?? []
   ).map((item) => {
    const code = item.lang

    return {
     ...item,
     lang: code
      ? languageMap[code] ?? {
         language_code: code,
         language: code.toUpperCase(),
        }
      : undefined,
    }
   })
   originalImageRef.current = image_url
   setFormState((prev) => ({
    ...prev,
    image: image_url,
    campaign_variants: mappedVariants ?? [],
    productName: product_name,
    productVariantSize: product_variant_size,
    name,
   }))
  },
  [languageMap]
 )

 const handleAdd = (data: Omit<TemplateRow, 'id'>) => {
  const newTemplate: TemplateRow = {
   ...data,
   id: Date.now(),
   is_temp: true,
  }
  updateState('campaign_variants', [
   ...templates,
   newTemplate,
  ])
 }

 const handleImageUpload = async (file: File) => {
  if (!file) return
  const localUrl = URL.createObjectURL(file)
  setLocalImagePreview(localUrl)
  setImageLoader(true)

  const formData = new FormData()
  formData.append('file', file)
  formData.append('file_type', 'image')
  formData.append('document_type', 'image')
  formData.append(
   'folder_name',
   'marketing/product_promotion'
  )
  formData.append('bucket', 'public')

  const res = await uploadFile(formData)

  if (res?.filepath) {
   updateState('image', res.filepath)
  }

  setImageLoader(false)
 }

 const buildPayload = () => {
  const hasSku = !!formState.sku

  const payload = {
   name: formState.name,
   entity_id: hasSku
    ? String(formState.sku?.id)
    : String(formState.productId),
   image_url: formState.image,
   entity_type: hasSku ? 'PRODUCT_VARIANT' : 'PRODUCT',
   campaign_variants: formState.campaign_variants?.map(
    (t) => ({
     language: t.lang?.language_code,
     template_name: t.template_name,
     template_text: t.template_text,
    })
   ),
  }
  return payload
 }

 const buildUpdatePayload = () => {
  const existingActive = templates
   .filter((t) => !t.is_temp)
   .map((t) => ({
    id: t.id,
    template_text: t.template_text,
    is_active: true,
   }))

  const newlyAdded = templates
   .filter((t) => t.is_temp)
   .map((t) => ({
    language: t.lang?.language_code,
    template_name: t.template_name,
    template_text: t.template_text,
    is_active: true,
   }))

  const payload = {
   id: Number(campaignId),
   campaign_variants: [...existingActive, ...newlyAdded],
   ...(formState.image !== originalImageRef.current
    ? {image_url: formState.image}
    : {}),
  }
  return payload
 }

 const handleSubmit = async () => {
  if (!isFormValid) return
  if (isEditMode) {
   const payload = buildUpdatePayload()
   const res = await updateCampaignApi(payload)
   if (res?.data) {
    navigate(-1)
   }
   return
  }

  const payload = buildPayload()
  const res = await createPromotionApi(payload)
  if (res.success) {
   navigate(-1)
  }
 }

 const handleEdit = (updated: Omit<TemplateRow, 'id'>) => {
  if (!editRow) return
  updateState(
   'campaign_variants',
   templates.map((t) => {
    if (t.id !== editRow?.id) return t
    return {...t, ...updated}
   })
  )
 }

 const handleDelete = async (id: number) => {
  if (templates.length <= 1) {
   toast.error(t('createPromotion.warningText'))
   return
  }
  const row = templates.find((t) => t.id === id)
  if (!row) return
  if (row.is_temp) {
   updateState(
    'campaign_variants',
    templates.filter((t) => t.id !== id)
   )
   return
  }
  setEditRow(row)
  const success = await deleteCampaignVariantApi(id)
  if (success && campaignId) {
   await loadCampaignDetail(Number(campaignId))
  }
 }

 const debouncedGetProducts = useRef(
  debounce((value: string) => {
   void getProducts(value)
  }, 500)
 ).current

 const handleNameChange = (value: string) => {
  updateState('productName', value)
  updateState('productId', undefined)
  setShowDropdown(true)
  updateState('sku', null)
  if (value.length >= 2) {
   debouncedGetProducts(value)
  }
 }

 const handleSelectProduct = (product: Product) => {
  updateState('productName', product.name)
  updateState('productId', product.id)
  updateState(
   'product_image_url',
   product.product_image_url
  )
  updateState('sku', null)
  setShowDropdown(false)
 }

 useEffect(() => {
  setHideParent(false)
  const debounced = debouncedGetProducts
  return () => {
   debounced.cancel()
  }
 }, [debouncedGetProducts, setHideParent])

 useEffect(() => {
  if (campaignId && Object.keys(languageMap).length) {
   void loadCampaignDetail(Number(campaignId))
  }
 }, [campaignId, languageMap, loadCampaignDetail])

 useEffect(() => {
  const loadLanguages = async () => {
   const res = await fetchLanguageList()

   const map: Record<string, LanguageItem> = {}
   res.forEach((l) => {
    map[l.language_code] = l
   })

   setLanguageMap(map)
  }
  void loadLanguages()
 }, [])

 useEffect(() => {
  return () => {
   if (localImagePreview) {
    URL.revokeObjectURL(localImagePreview)
   }
  }
 }, [localImagePreview])

 return (
  <>
   <p className='my-6 text-[20px] font-bold'>
    {t('createPromotion.title')}
   </p>
   <VariableNote variables={VARIABLE_LIST} />
   <div className='my-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
    <div
     className='relative rounded-xl border px-4 py-3 focus-within:border-black'
     ref={wrapperRef}
    >
     <p className='text-xs text-[#3F76FF] font-medium'>
      {t('createPromotion.productName')}
      <span className='text-red-500'>*</span>
     </p>

     <input
      value={formState.productName ?? ''}
      onChange={(e) => handleNameChange(e.target.value)}
      onBlur={() => {
       if (!formState.productId) {
        updateState('productName', '')
        setProducts([])
       }
      }}
      placeholder={t('createPromotion.productNameLabel')}
      className='mt-1 w-full bg-transparent outline-none'
      disabled={isEditMode}
     />

     {showDropdown && products.length > 0 && (
      <div
       className='absolute z-50 top-full left-0 right-0 mt-1 
      bg-white shadow-lg rounded-lg border max-h-56 overflow-y-auto hide-scrollbar'
      >
       {products.map((p) => (
        <div
         key={p.id}
         onMouseDown={() => handleSelectProduct(p)}
         className='px-4 py-2 cursor-pointer hover:bg-gray-100'
        >
         {p.name}
        </div>
       ))}
      </div>
     )}
    </div>
    <div>
     <SkuSizeDropdown
      disabled={isEditMode}
      value={formState.sku ?? null}
      onChange={(val) => updateState('sku', val)}
      variants={
       formState?.productId
        ? products.find(
           (p) => p.id === Number(formState.productId)
          )?.variants ?? []
        : []
      }
      productVariantSize={formState.productVariantSize}
     />
    </div>
    <div className='rounded-xl border bg-[#F9FAFB] px-4 py-3  focus-within:border-black'>
     <p className='text-xs text-[#3F76FF] font-medium'>
      {t('createPromotion.promotionName')}
      <span className='text-red-500'>*</span>
     </p>

     <input
      value={formState.name ?? ''}
      onChange={(e) => updateState('name', e.target.value)}
      placeholder={t('createPromotion.promotionNameLable')}
      className='mt-1 w-full bg-transparent  outline-none'
      disabled={isEditMode}
     />
    </div>
    <div />
   </div>
   <p className='mb-2 text-[16px] font-semibold'>
    {t('createPromotion.templateText')}
    <span className='text-red-500'>*</span>
   </p>

   <TableTemplate
    templates={templates}
    setEditRow={setEditRow}
    setShowDialog={setShowDialog}
    handleDelete={handleDelete}
   />

   <UploadThemeImage
    onImageSelect={handleImageUpload}
    loading={imageLoader}
    imageUrl={formState?.image}
    textFlag={true}
   />
   <div
    style={{
     flex: 1,
     borderColor: '#E6EBF0',
     borderWidth: 1,
     marginTop: 20,
    }}
   />

   <div
    className={`mt-8 flex gap-4 ${
     canShowPreview ? 'justify-between' : 'justify-end'
    }`}
   >
    <div>
     {canShowPreview && (
      <button
       onClick={() => {
        setShowPreview(true)
       }}
       className={`
      px-6 py-2 font-medium
      rounded-lg 
      transition-all duration-200
      bg-[#F5FAFF] text-[#4F7BD9] border-[1px] border-[#4E83E4]
    `}
      >
       {t('preview.viewPreview')}
      </button>
     )}
    </div>

    <button
     onClick={handleSubmit}
     className={`
      px-6 py-2 rounded-lg font-medium transition
      ${
       !isFormValid
        ? 'bg-[#C5CED6] text-white cursor-not-allowed'
        : 'bg-[#5E7CE2] text-white'
      }
    `}
    >
     {t('createPromotion.publishTemplate')}
    </button>
   </div>

   {showDialog && (
    <TemplateDialog
     editData={editRow}
     onClose={() => setShowDialog(false)}
     templates={templates}
     onSubmit={editRow ? handleEdit : handleAdd}
    />
   )}
   {showPreview && (
    <Preview
     open={showPreview}
     onClose={() => {
      if (languageVariants.length > 0) {
       setSelectedLang(languageVariants[0].language_code)
      } else {
       setSelectedLang('')
      }

      setShowPreview(false)
     }}
     languageVariants={languageVariants}
     selectedLang={selectedLang}
     setSelectedLang={setSelectedLang}
    >
     <PromotionImageSection
      formState={{
       ...formState,
       image: localImagePreview ?? formState.image,
      }}
      selectedTemplate={selectedTemplate}
     />
    </Preview>
   )}
  </>
 )
}

export default function CreatePromotionWrapper() {
 const {t} = useTranslation()
 const [hidePartent, setHideParent] = useState(true)
 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-10 bg-white w-full min-h-full'>
    {!hidePartent && (
     <PageTitle
      breadcrumbContainerClass={
       'flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0] border-b'
      }
      breadcrumbs={[
       {
        title: t('breadCrumbs.home'),
        path: '/home-page',
        isActive: false,
       },
       {
        title: t('breadCrumbs.activityManagement'),
        path: '/activity-management',
        isActive: false,
       },
       {
        title: t('breadCrumbs.productPromotion'),
        path: '/activity-management/product-promotion',
        isActive: false,
       },
       {
        title: t(
         'breadCrumbs.createRecommendationPromotion'
        ),
        path: '',
        isActive: true,
       },
      ]}
      description='Welcome'
     >
      {t('breadCrumbs.createRecommendationPromotion')}
     </PageTitle>
    )}
    <CreatePromotion
     hidePartent={hidePartent}
     setHideParent={setHideParent}
    />
   </div>
  </div>
 )
}
